import React, { useCallback, useEffect, useState } from 'react'
import { ContratoProtocol } from '../../Pages/Extratos/interfaces'
import { Container, Content, UnidadesRecebiveis, Table, Agenda } from './styles'
import { FiDownload, FiPrinter } from 'react-icons/fi'
import api, { operation } from '../../services/axios'
import { cnpjCpfMask, parseDate } from '../../utils/masks'
import { CSVLink } from 'react-csv'
import { arranjosPrincipais, getIcon } from '../../utils/arranjos'
import { utils, writeFileXLSX } from 'xlsx'
import contractPdf from '../../reports/contracts/contractPdf'
import { calcDiffDays } from '../../utils/date'
import { get } from 'lodash'

import { useBancos } from '../../hooks/bancos'
import { Link } from 'react-router-dom'
import { selectedMenu } from '../../store/modules/menu/actions'
import { useDispatch } from 'react-redux'
import { useCredenciadoras } from '../../hooks/credenciadoras'
import dayjs from '../../libs/dayjs'
import proofOfAdvancePayment from '../../reports/contracts/proofOfAdvancePayment'

interface ContractsModalProps {
    contract: ContratoProtocol
    isExtract?: boolean
}

interface GarantiaProtocol {
    DP_agencia: string
    DP_compe: string
    DP_ispb: string
    DP_nomeTitular: string | null
    DP_numeroConta: string
    DP_numeroDocumentoTitular: string
    DP_tipoConta: string
    DefUR_dataFim: string
    DefUR_dataInicio: string
    DefUR_documentoTitular: string
    DefUR_documentoUsuarioFinalRecebedor: string
    DefUR_listaCnpjCredenciadora: string
    DefUR_listaCodigoArranjoPagamento: string
    contratoId: string
    id: number
    referenciaExterna: string
    regrasDivisao: string
    tipoDistribuicao: string
    valorAOnerar: string
}

const headers = [
    { label: 'Referência externa', key: 'RefExtContrato' },
    { label: 'Tipo de Contrato', key: 'tipoContrato' },
    { label: 'Data de Assinatura', key: 'dataAssinatura' },
    { label: 'Credenciadora', key: 'crendeciadora' },
    { label: 'Cnpj do participante', key: 'cnpjParticipante' },
    { label: 'Canal', key: 'canal' },
    { label: 'Cnpj do pagador', key: 'cnpjPagador' },
    { label: 'Pagador', key: 'pagador' },
    { label: 'Cnpj do recebedor', key: 'cnpjRecebedor' },
    { label: 'Recebedor', key: 'recebedor' },
    { label: 'Banco', key: 'banco' },
    { label: 'Compe', key: 'compe' },
    { label: 'Ispb', key: 'ispb' },
    { label: 'Agência', key: 'agencia' },
    { label: 'Conta', key: 'conta' },
    { label: 'Tipo de conta', key: 'tipoConta' },
    { label: 'Data de vencimento da UR', key: 'DataVencimentoUR' },
    { label: 'Bandeira', key: 'bandeira' },
    { label: 'Valor da UR', key: 'valorUR' },
    { label: 'Valor contrato do total', key: 'valorTotalContrato' },
    { label: 'Situação', key: 'situacao' },
    { label: 'Observação', key: 'obs' },
]

interface Address {
    address: string
    number: string
    city: string
    state: string
}

const ContractsModal: React.FC<ContractsModalProps> = ({
    contract,
    isExtract = true,
}) => {
    const dispatch = useDispatch()
    const { bancos } = useBancos()
    const { todasCredenciadoras } = useCredenciadoras()

    const [garantias, setGarantias] = useState<GarantiaProtocol[]>([])
    const [address, setAddress] = useState<Address>()

    const getGarantias = useCallback(async () => {
        operation.attempt(async (currentAttempt) => {
            try {
                const { data } = await api.get(
                    `/garantias/${contract.referenciaExterna}`
                )
                setGarantias(data)

                const response = await api.get(
                    `/institution/address/${contract.cnpjPagador}`
                )
                setAddress(response.data)
            } catch (e: any) {
                // toast.error('Erro inesperado')
                const status = get(e, 'response.status', '')
                // toast.error(erro)
                if (status === 401) {
                    if (operation.retry(e)) return
                }
            }
        })
    }, [contract])

    useEffect(() => {
        getGarantias()
    }, [getGarantias])

    const totalDias = garantias.reduce((total, garantia) => {
        const diffDays = calcDiffDays(garantia.DefUR_dataFim)
        return total + diffDays
    }, 0)

    const prazoMedio = totalDias / garantias.length
    const prazoMedioDescription =
        prazoMedio === 0
            ? 'Hoje'
            : prazoMedio > 0
            ? `${Math.round(prazoMedio)} dias`
            : 'Liquidado'

    const dataCsv = garantias.map((garantia) => {
        const banco = bancos[garantia.DP_compe]
        const credenciadora = todasCredenciadoras[contract.credenciadora]
        const bandeira =
            arranjosPrincipais[
                garantia.DefUR_listaCodigoArranjoPagamento as keyof typeof arranjosPrincipais
            ]
        return {
            RefExtContrato: contract.referenciaExterna,
            tipoContrato: contract.tipoContrato,
            dataAssinatura: contract.dataAssinatura,
            crendeciadora: credenciadora,
            cnpjParticipante: contract.cnpjParticipante,
            canal: contract.canal,
            cnpjPagador: contract.cnpjPagador,
            pagador: contract.nomePagador,
            cnpjRecebedor: contract.cnpjRecebedor,
            recebedor: contract.nomeRecebedor,
            banco: banco,
            compe: garantia.DP_compe,
            ispb: garantia.DP_ispb,
            agencia: garantia.DP_agencia,
            conta: garantia.DP_numeroConta,
            tipoConta: garantia.DP_tipoConta,
            DataVencimentoUR: garantia.DefUR_dataFim,
            bandeira: bandeira,
            valorUR: Number(garantia.valorAOnerar).toFixed(2),
            valorTotalContrato: Number(contract.saldoDevedor).toFixed(2),
            situacao: !contract.visibilidade
                ? 'Pendente de confirmação da registradora'
                : contract.situacao,
            obs: contract.observacao,
        }
    })
    // const crendeciadora: keyof typeof todasCredenciadoras =
    //     contract.credenciadora as keyof typeof todasCredenciadoras
    const nomeCredeciadora = todasCredenciadoras[contract?.credenciadora]

    function renderTableURsRow() {
        return garantias.map((garantia: GarantiaProtocol) => {
            const diffDays = calcDiffDays(garantia.DefUR_dataFim)
            return (
                <tr key={garantia.id}>
                    <td>{cnpjCpfMask(contract.credenciadora)}</td>
                    <td>{nomeCredeciadora}</td>
                    <td>{cnpjCpfMask(contract.cnpjRecebedor)}</td>
                    <td>
                        {parseDate(garantia.DefUR_dataFim, true)}{' '}
                        {diffDays === 0
                            ? '(hoje)'
                            : diffDays > 0
                            ? `(${diffDays} dias)`
                            : `(Há ${diffDays * -1} dias)`}
                    </td>
                    <td>
                        <img
                            height={35}
                            width={60}
                            src={getIcon(
                                garantia.DefUR_listaCodigoArranjoPagamento
                            )}
                            alt="Icon bandeira cartão"
                        />
                    </td>
                    <td>
                        {Number(garantia.valorAOnerar).toLocaleString('pt-br', {
                            style: 'currency',
                            currency: 'BRL',
                        })}
                    </td>
                </tr>
            )
        })
    }

    const exportFile = useCallback(() => {
        const dataXlsx = garantias.map((garantia) => {
            const banco = bancos[garantia.DP_compe]
            const credenciadora = todasCredenciadoras[contract.credenciadora]
            const bandeira =
                arranjosPrincipais[
                    garantia.DefUR_listaCodigoArranjoPagamento as keyof typeof arranjosPrincipais
                ]
            return {
                'Referência externa': contract.referenciaExterna,
                'Tipo do contrato': contract.tipoContrato,
                'Data de assinatura': contract.dataAssinatura,
                Crendeciadora: credenciadora,
                'Cnpj do participante': contract.cnpjParticipante,
                Canal: contract.canal,
                'Cnpj do pagador': contract.cnpjPagador,
                Pagador: contract.nomePagador,
                'Cnpj do recebedor': contract.cnpjRecebedor,
                Recebedor: contract.nomeRecebedor,
                Banco: banco,
                Compe: garantia.DP_compe,
                Ispb: garantia.DP_ispb,
                Agência: garantia.DP_agencia,
                Conta: garantia.DP_numeroConta,
                'Tipo de conta': garantia.DP_tipoConta,
                'Data de vencimento da UR': garantia.DefUR_dataFim,
                Bandeira: bandeira,
                'Valor da UR': Number(garantia.valorAOnerar).toFixed(2),
                'Valor total do contrato': Number(
                    contract.saldoDevedor
                ).toFixed(2),
                'Situação do contrato': !contract.visibilidade
                    ? 'Pendente de confirmação da registradora'
                    : contract.situacao,
                Observação: contract.observacao,
            }
        })
        const ws = utils.json_to_sheet(dataXlsx)
        const wb = utils.book_new()
        utils.book_append_sheet(wb, ws, 'Data')
        writeFileXLSX(wb, 'Extrato.xlsx')
    }, [garantias, contract, bancos, todasCredenciadoras])

    function renderContractType(contract: ContratoProtocol) {
        if (contract.tipoContrato === 'Taxa') {
            return 'Comissão'
        } else if (
            contract.recorrenciaId &&
            contract.linkDePagamentoId === null
        ) {
            return contract.compara_pagador ? (
                <Link
                    to={`/recorrencia/${contract.recorrenciaId}`}
                    onClick={() =>
                        dispatch(
                            selectedMenu({
                                mainMenu: 'Recorrência',
                                subMenu: '',
                                currentLink: '/recorrencia',
                            })
                        )
                    }
                >
                    Recorrência
                </Link>
            ) : (
                'Recorrência'
            )
        } else if (contract.linkDePagamentoId) {
            const criadorCobranca = contract.compara_pagador ? 'false' : 'true'

            if (contract.recorrenciaId) {
                return (
                    <>
                        <Link
                            to={`/cobranca/${contract.linkDePagamentoId}?criadorCobranca=${criadorCobranca}`}
                            onClick={() =>
                                dispatch(
                                    selectedMenu({
                                        mainMenu: 'Cobranças',
                                        subMenu: '',
                                        currentLink: '/cobrancas',
                                    })
                                )
                            }
                        >
                            Cobrança
                        </Link>{' '}
                        /
                        {contract.compara_pagador ? (
                            <Link
                                to={`/recorrencia/${contract.recorrenciaId}`}
                                onClick={() =>
                                    dispatch(
                                        selectedMenu({
                                            mainMenu: 'Recorrência',
                                            subMenu: '',
                                            currentLink: '/recorrencia',
                                        })
                                    )
                                }
                            >
                                {' '}
                                Recorrência
                            </Link>
                        ) : (
                            ' Recorrência'
                        )}
                    </>
                )
            }
            return (
                <Link
                    to={`/cobranca/${contract.linkDePagamentoId}?criadorCobranca=${criadorCobranca}`}
                    onClick={() =>
                        dispatch(
                            selectedMenu({
                                mainMenu: 'Cobranças',
                                subMenu: '',
                                currentLink: '/cobrancas',
                            })
                        )
                    }
                >
                    Cobrança
                </Link>
            )
        } else if (contract.anticipationId) {
            return 'Antecipação'
        } else {
            return 'Pagamento'
        }
    }

    return (
        <Container>
            <div className="actions">
                <h3>Ações</h3>
                <div className="exports">
                    {contract.anticipationId !== null && (
                        <button
                            onClick={() =>
                                contract.anticipationId !== null &&
                                proofOfAdvancePayment(
                                    contract,
                                    garantias,
                                    address,
                                    isExtract,
                                    bancos,
                                    todasCredenciadoras
                                )
                            }
                        >
                            <FiPrinter size={18} /> Antecipação
                        </button>
                    )}
                    <button
                        onClick={() =>
                            contractPdf(
                                contract,
                                garantias,
                                address,
                                isExtract,
                                bancos,
                                todasCredenciadoras
                            )
                        }
                    >
                        <FiPrinter size={18} /> Contrato
                    </button>
                    <CSVLink
                        data={dataCsv}
                        filename="Extrato.csv"
                        headers={headers}
                    >
                        <button>
                            <FiDownload size={18} /> Exportar CSV
                        </button>
                    </CSVLink>
                    <button onClick={exportFile}>
                        <FiDownload size={18} /> Exportar XLSX
                    </button>
                </div>
            </div>

            <Content>
                <div className="input-group">
                    <h3>Valor pago</h3>
                    <div className="wrapper">
                        <p>
                            {Number(contract.saldoDevedor).toLocaleString(
                                'pt-br',
                                {
                                    style: 'currency',
                                    currency: 'BRL',
                                }
                            )}
                        </p>
                    </div>
                </div>

                <div className="input-group">
                    <div className="wrapper info">
                        <p>
                            <strong className="situation">
                                Situação contrato:{' '}
                            </strong>
                            {!contract.visibilidade
                                ? 'Pendente de confirmação da registradora'
                                : contract.situacao}
                        </p>
                        <p>
                            <strong className="op">Tipo do contrato: </strong>
                            {renderContractType(contract)}
                        </p>
                        <p>
                            <strong className="op">Número da operação: </strong>
                            {contract.numeroOperacao}
                        </p>
                        <p>
                            <strong className="canal">Canal de origem: </strong>
                            {contract.canal}
                        </p>
                    </div>
                </div>

                <div className="input-group">
                    <h3>Dados do recebedor</h3>
                    <div className="receiver">
                        <div className="wrapper">
                            <strong>Para: </strong>
                            <p>{contract.nomeRecebedor}</p>
                        </div>
                        <div className="wrapper">
                            <p>
                                <strong>CPF/CNPJ:</strong>{' '}
                                {cnpjCpfMask(contract.cnpjRecebedor || '')}
                            </p>
                        </div>
                    </div>
                    <h4>Domicílio bancário: </h4>
                    <div className="bank-address">
                        <div className="wrapper">
                            <p>Banco: {bancos[garantias[0]?.DP_compe]}</p>
                        </div>
                        <div className="wrapper">
                            <p>Agência: {garantias[0]?.DP_agencia}</p>
                        </div>
                        <div className="wrapper">
                            <p>
                                Número da conta: {garantias[0]?.DP_numeroConta}
                            </p>
                        </div>
                    </div>
                    <div className="wrapper">
                        <p>Tipo de conta: {garantias[0]?.DP_tipoConta}</p>
                    </div>
                </div>

                {/* <h4>Contato: </h4>
                <div className="flex">
                    <div className="wrapper">
                        <p><span>Email: </span>{contract.emailRecebedor}</p>
                    </div>
                    <div className="wrapper">
                        <p><span>Telefone: </span>{foneMask(contract.foneRecebedor)}</p>
                    </div>
                </div> */}

                <div className="input-group">
                    <h3>Dados do pagador</h3>
                    <div className="wrapper">
                        <strong>De: </strong>
                        <p>{contract.nomePagador}</p>
                    </div>
                    <div className="wrapper">
                        <p>
                            <strong>CPF/CNPJ </strong>
                            {cnpjCpfMask(contract.cnpjPagador || '')}
                        </p>
                    </div>
                </div>

                <div className="input-group">
                    <div className="wrapper details">
                        <p>
                            Data e hora do pagamento:{' '}
                            {dayjs(contract.createdAt).format(
                                'DD/MM/YYYY - HH:mm:ss'
                            )}
                        </p>
                        <p>
                            Código de referência do pagamento:{' '}
                            {contract.referenciaExterna}
                        </p>
                        {contract?.comissao?.referenciaExterna && (
                            <p>
                                Código de referência do serviço:{' '}
                                {contract?.comissao?.referenciaExterna}
                            </p>
                        )}
                        <p>Observação: {contract.observacao || 'n/a'}</p>
                    </div>
                </div>
            </Content>
            <Agenda>
                <h2 className="center">Agenda</h2>
                <div className="details">
                    <p>
                        <strong>Prazo médio:</strong>{' '}
                        {/* {Math.round(prazoMedio) || 0} dias */}
                        {prazoMedioDescription}
                    </p>
                    <p>
                        <strong>Quantidade de URs:</strong> {garantias.length}
                    </p>
                    <p>
                        <strong>Credenciadora:</strong> {nomeCredeciadora}
                    </p>
                    <p>
                        <strong>Importante:</strong> O(s) pagamento(s) serão
                        efetuados pela credenciadora na data de vencimentos das
                        URs.
                    </p>
                </div>
                <h2>Detalhes Unidades Recebíveis</h2>
            </Agenda>

            <UnidadesRecebiveis>
                <Table>
                    <thead>
                        <tr>
                            <th>CNPJ Credenciadora</th>
                            <th>Credenciadora</th>
                            <th>CNPJ Recebedor</th>
                            <th>Data Vencimento</th>
                            <th>Bandeira</th>
                            <th>Valor</th>
                        </tr>
                    </thead>
                    <tbody>{renderTableURsRow()}</tbody>
                </Table>
            </UnidadesRecebiveis>
        </Container>
    )
}

export default ContractsModal
